<script setup lang="ts">
import { LoaderCircleIcon, TriangleAlertIcon } from 'lucide-vue-next'
import {nextTick, onMounted, ref} from 'vue'
import type {ProductResponse} from "../../../responses/ProductResponse";
import {label} from "../../mixins/translations";
import {useCourseStore} from "../../stores/course";
import {useGeoLocationStore} from "../../stores/geolocation";
import Course from "./Course.vue";
import CourseFilter from "./CourseFilter.vue";

interface Props{
    product: ProductResponse
    filters: { [key: string]: string }
}
const props = defineProps<Props>();
const courseStore = useCourseStore();
const geoLocationStore = useGeoLocationStore();
const loading = ref(true);

onMounted(() => {
    getCourses();
});

const getCourses = () => {
    courseStore.getCourses(props.product.id)
        .then(() => {
            loading.value = false;
        }).catch(console.error);
}

const loadAll = async () => {
    courseStore.toggleAll = true;
    getCourses();
}

geoLocationStore.events.on('radiusChanged', () => {
    getCourses();
});
geoLocationStore.events.on('locationChanged', () => {
    getCourses();
});
courseStore.events.on('monthChanged', () => {
    getCourses();
});
</script>
<template>
    <section class="relative py-6 lg:py-14 overflow-hidden">

        <div class="container mx-auto px-4 max-w-7xl">
            <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h2 class="text-2xl font-extrabold">{{ label('site.product.available_dates') }}</h2>
                    <p class="text-sm text-black">
                        {{ courseStore.totalCount }} {{ label('site.product.results') }}
                    </p>
                </div>

                <course-filter :filters="filters"></course-filter>
            </div>

            <div class="flex justify-center p-4 gap-2" v-if="loading">
                <LoaderCircleIcon class="w-8 h-8 animate-spin"></LoaderCircleIcon>
            </div>
            <div class="space-y-2 lg:space-y-4">
                <course v-for="(course, index) in courseStore.courses" :course="course" :key="course.id" :product="product"></course>
            </div>
            <div v-if="courseStore.courses.length === 0" class="flex justify-center p-4 gap-2">
                <TriangleAlertIcon></TriangleAlertIcon>
                <div>{{ label('site.product.no_results_found') }}</div>
            </div>

            <div class="mt-10 flex justify-center" v-show="courseStore.totalCount > courseStore.courses.length">
                <button
                    class="rounded-md border px-8 py-3 font-semibold transition"
                    :class="courseStore.toggleAll ? 'cursor-default border-gray-300 text-gray-400' : 'border-primary text-primary hover:bg-primary hover:text-white bg-white'"
                    :disabled="courseStore.toggleAll"
                    @click.prevent="loadAll">
                    {{ courseStore.toggleAll ? label('site.product.all_dates_shown') : label('site.product.show_all_dates') }}
                </button>
            </div>

        </div>
    </section>
</template>
